// components/TopMenu/Costs.js (Rent.js)
import { useState, useEffect } from 'react';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { SERVER_URL } from '../../constants.js';
import { lightBlue } from '@mui/material/colors';
import { formatLocalDateOnly } from '../shared utils/dateFormatters.js';


export default function Rent({ selectedYear }) {
  const [residentCount, setResidentCount] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState('All');
  const [costRates, setCostRates] = useState({});
  const [bookingIncome, setBookingIncome] = useState([]);

  const gbp = (v) =>
    Number(v || 0).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' });

  // Fetch cost rates lookup for the given year
  const fetchCostRates = async year => {
    try {
      const token = sessionStorage.getItem('bearer');
      const url = `${SERVER_URL}api/rentcosts?year=${year}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 204) {
        setCostRates({});
        return;
      } else if (!res.ok) {
        throw new Error('Failed to fetch cost rates');
      }
      const data = await res.json();
      const lookup = {};
      data.forEach(({ campus, residentType, rate }) => {
        lookup[`${campus}-${residentType.toLowerCase()}`] = rate;
      });
      setCostRates(lookup);
    } catch (err) {
      console.error(err);
      setCostRates({});
    }
  };

  // Bulk fetch for resident counts over the full year
  const fetchCounts = async (start, end) => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('bearer');
      const url = `${SERVER_URL}api/residentcounts?start=${start}&end=${end}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 204) {
        setResidentCount([]);
      } else if (!res.ok) {
        throw new Error('Failed to fetch resident counts');
      } else {
        setResidentCount(await res.json());
      }
    } catch (err) {
      console.error(err);
      setResidentCount([]);
    } finally {
      setLoading(false);
    }
  };

  // Booking income per campus for the year
  const fetchBookingIncome = async year => {
    try {
      const token = sessionStorage.getItem('bearer');
      const res = await fetch(`${SERVER_URL}api/bookingincome?year=${year}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 204) {
        setBookingIncome([]);
        return;
      } else if (!res.ok) {
        throw new Error('Failed to fetch booking income');
      }
      const data = await res.json();
      setBookingIncome(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setBookingIncome([]);
    }
  };

  // When year changes, load rates, counts and booking income
  useEffect(() => {
    fetchCostRates(selectedYear);
    fetchCounts(`${selectedYear}-01-01`, `${selectedYear}-12-31`);
    fetchBookingIncome(selectedYear);
  }, [selectedYear]);

  // Calculate totals per campus + overall
  const calculateTotals = () => {
    const acc = { Overall: 0 };
    residentCount.forEach(item => {
      const key = `${item.campus}-${item.residentType.toLowerCase()}`;
      const rate = costRates[key] || 0;
      const cost = item.residentCount * rate;
      acc[item.campus] = (acc[item.campus] || 0) + cost;
      acc.Overall += cost;
    });
    return acc;
  };

  const totals = calculateTotals();

  const campuses = ['All', ...Array.from(new Set(residentCount.map(i => i.campus)))];
  const handleTabChange = (_, newVal) => setTabValue(newVal);
  const dataFor = campus => (campus === 'All' ? residentCount : residentCount.filter(i => i.campus === campus));

  // CSV export handler
  const exportCsv = () => {
    const headers = ['date', 'campus', 'residentType', 'residentCount', 'rate', 'cost'];
    const rows = residentCount.map(item => {
      const rate = costRates[`${item.campus}-${item.residentType.toLowerCase()}`] || 0;
      return [
        formatLocalDateOnly(new Date(item.givenDate)),
        item.campus,
        item.residentType,
        item.residentCount,
        rate,
        rate * item.residentCount
      ];
    });
    const csvContent = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `rent_${selectedYear}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <section className="garamond">
      {/* Centered title */}
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <h2>Rent for {selectedYear}</h2>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Totals table */}
          <Box sx={{ maxWidth: 400, ml: 2, mb: 3 }}>
            <Box sx={{ fontWeight: 'bold', mb: 0.5 }}>Rent cost</Box>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableBody>
                  {Object.entries(totals)
                    .filter(([campus]) => campus !== 'Overall')
                    .map(([campus, value]) => (
                      <TableRow key={campus}>
                        <TableCell>{campus}</TableCell>
                        <TableCell align="right">£ {value}</TableCell>
                      </TableRow>
                    ))}
                  <TableRow>
                    <TableCell><strong>Overall</strong></TableCell>
                    <TableCell align="right"><strong>£ {totals.Overall}</strong></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Booking income table */}
          <Box sx={{ maxWidth: 400, ml: 2, mb: 3 }}>
            <Box sx={{ fontWeight: 'bold', mb: 0.5 }}>Booking income</Box>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableBody>
                  {bookingIncome.map(({ campus, income }) => (
                    <TableRow key={campus}>
                      <TableCell>{campus}</TableCell>
                      <TableCell align="right">{gbp(income)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell><strong>Overall</strong></TableCell>
                    <TableCell align="right">
                      <strong>
                        {gbp(bookingIncome.reduce((s, r) => s + Number(r.income || 0), 0))}
                      </strong>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          {/* Detail tabs */ }
          <TabContext value={tabValue}>
            <Paper square elevation={0} sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <TabList
                onChange={handleTabChange}
                aria-label="Rent by campus"
                variant="scrollable"
                scrollButtons="auto"
                textColor="primary"
                indicatorColor="primary"
              >
                {campuses.map(c => (
                  <Tab
                    key={c}
                    label={c}
                    value={c}
                    sx={{ flexGrow: 0.5, border: 1, borderRadius: 1, mr: 0.5 }}
                  />
                ))}
              </TabList>
            </Paper>

            {campuses.map(campus => (
              <TabPanel key={campus} value={campus}>
                <TableContainer component={Paper} sx={{ margin: '0 auto', maxWidth: 1900 }}>
                  <Table size="small" sx={{ minWidth: 800, margin: '0 auto' }} aria-label={`${campus} rent details`}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: lightBlue[100], border: 1.5 }}>
                        <TableCell sx={{ border: 1.5, bgcolor: lightBlue[100], color: 'black' }} align="center">Date</TableCell>
                        <TableCell sx={{ border: 1.5, bgcolor: lightBlue[100], color: 'black' }} align="center">Campus</TableCell>
                        <TableCell sx={{ border: 1.5, bgcolor: lightBlue[100], color: 'black' }} align="center">Type</TableCell>
                        <TableCell sx={{ border: 1.5, bgcolor: lightBlue[100], color: 'black' }} align="center">Count</TableCell>
                        <TableCell sx={{ border: 1.5, bgcolor: lightBlue[100], color: 'black' }} align="center">Rate (£)</TableCell>
                        <TableCell sx={{ border: 1.5, bgcolor: lightBlue[100], color: 'black' }} align="center">Cost (£)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dataFor(campus).map((item, idx) => {
                        const rate = costRates[`${item.campus}-${item.residentType.toLowerCase()}`] || 0;
                        return (
                          <TableRow key={idx} hover>
                            <TableCell align="center">{formatLocalDateOnly(new Date(item.givenDate))}</TableCell>
                            <TableCell sx={{ border: 1.5 }} align="center">{item.campus}</TableCell>
                            <TableCell sx={{ border: 1.5 }} align="center">{item.residentType}</TableCell>
                            <TableCell sx={{ border: 1.5 }} align="center">{item.residentCount}</TableCell>
                            <TableCell sx={{ border: 1.5 }} align="center">{rate}</TableCell>
                            <TableCell sx={{ border: 1.5 }} align="center">{rate * item.residentCount}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </TabPanel>
            ))}
          </TabContext>
        </>
      )}

      {/* CSV Export below table */}
      {!loading && residentCount.length > 0 && (
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <IconButton onClick={exportCsv} aria-label="Export CSV">
            <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24">
              <path d="M5 20h14v-2H5v2zn7-18L5.33 9h3.99v4h4.36V9h3.99L12 2z"/>
            </svg>
          </IconButton>
        </Box>
      )}

      {/* Auth status */}
      <Box sx={{ textAlign: 'center', mt: 1 }}>
        <p style={{ color: '#999', fontSize: '10px' }}>Authenticated: {sessionStorage.getItem('isAuthenticated')}</p>
      </Box>
    </section>
  );
}
